import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { DataStructure } from "@/components/StaticTable";
import TextField from "@/components/TextField";

type SetValueType = React.Dispatch<
  React.SetStateAction<number | string | Date>
>;

interface ValeurSelectProps<T> {
  dataStructure: DataStructure<T>[];
  selectedFiltreKey: String | null;
  isBetweenOperatorSelected: () => boolean;
  isNullOperatorSelected: () => boolean;
  filterValue: number | string | Date;
  filterValueMin: number | string | Date;
  filterValueMax: number | string | Date;
  setFilterValue: SetValueType;
  setFilterValueMin: SetValueType;
  setFilterValueMax: SetValueType;
  setErroneMinMax: React.Dispatch<React.SetStateAction<boolean>>;
}

export function ValeurSelect<T>({
  dataStructure,
  selectedFiltreKey,
  isBetweenOperatorSelected,
  isNullOperatorSelected,
  filterValue,
  filterValueMin,
  filterValueMax,
  setFilterValue,
  setFilterValueMin,
  setFilterValueMax,
  setErroneMinMax,
}: ValeurSelectProps<T>) {
  const { t } = useTranslation();

  let verifieValeursMinMax = () => {
    const isValeursRenseignes =
      isBetweenOperatorSelected() && filterValueMin && filterValueMax;

    if (getRowType() === "number" && isValeursRenseignes) {
      const min = Number(filterValueMin);
      const max = Number(filterValueMax);

      setErroneMinMax(!isNaN(min) && !isNaN(max) && min > max);
    }

    if (getRowType() === "date" && isValeursRenseignes) {
      if (new Date(filterValueMin) > new Date(filterValueMax))
        setErroneMinMax(true);
      else setErroneMinMax(false);
    }
  };

  useEffect(() => {
    verifieValeursMinMax();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValueMin, filterValueMax]);

  const getRowType = () => {
    for (const ds of dataStructure) {
      if (!ds.filter) continue;

      const filters = Array.isArray(ds.filter) ? ds.filter : [ds.filter];

      // @ts-ignore
      const matchingFilter = filters.find((f) => f.key === selectedFiltreKey);
      if (matchingFilter) {
        return matchingFilter.rowType;
      }
    }
    return undefined;
  };

  const handleFilterValueChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    updateFunc: React.Dispatch<React.SetStateAction<number | string | Date>>
  ) => {
    if (getRowType() === "number") {
      let value = event.target.value.trim();

      if (!/^\d*$/.test(value)) return;
      if (value.length > 9) return;

      if (/^0+$/.test(value)) value = "0";
      const numericValue = value === "" ? "" : parseInt(value, 10);

      return updateFunc(numericValue as number | string);
    }

    updateFunc(event.target.value);
  };

  if (isNullOperatorSelected()) return <></>;

  if (isBetweenOperatorSelected())
    return (
      <>
        <TextField
          label={t("common.dmri.filter.valueMin")}
          onChange={(
            e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
          ) => handleFilterValueChange(e, setFilterValueMin)}
          value={filterValueMin}
          placeholder={t("common.dmri.filter.placeHolderValueMin")}
          type={getRowType()}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label={t("common.dmri.filter.valueMax")}
          onChange={(
            e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
          ) => handleFilterValueChange(e, setFilterValueMax)}
          value={filterValueMax}
          placeholder={t("common.dmri.filter.placeHolderValueMax")}
          type={getRowType()}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </>
    );

  return (
    <TextField
      label={t("common.dmri.filter.value")}
      onChange={(
        e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
      ) => handleFilterValueChange(e, setFilterValue)}
      value={filterValue}
      placeholder={t("common.dmri.filter.placeHolderValue")}
      type={getRowType()}
      InputLabelProps={{ shrink: true }}
      fullWidth
    />
  );
}

export default ValeurSelect;
